import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, MessageCircle, MapPin, Calendar, Send } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";

export default function Contact() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        company: "",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);

    const onChange = (e) =>
        setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const onSubmit = (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Please fill in your name, email and message.");
            return;
        }
        setSubmitting(true);
        const subject = encodeURIComponent(
            `New enquiry from ${form.name}${
                form.company ? " (" + form.company + ")" : ""
            }`,
        );
        const body = encodeURIComponent(
            `${form.message}\n\n— ${form.name}\n${form.email}`,
        );
        window.location.href = `mailto:support@thooyra.in?subject=${subject}&body=${body}`;
        setTimeout(() => {
            toast.success("Opening your email client…");
            setSubmitting(false);
        }, 400);
    };

    return (
        <section
            data-testid="contact-section"
            id="contact"
            className="relative py-24 md:py-32"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
                <div className="lg:col-span-5">
                    <div className="text-xs uppercase tracking-widest text-muted-foreground mb-4">
                        Let&apos;s talk
                    </div>
                    <h2 className="font-display font-light text-4xl md:text-5xl tracking-tighter leading-tight">
                        Tell us about your{" "}
                        <span className="italic font-medium">project</span>.
                    </h2>
                    <p className="mt-6 text-muted-foreground leading-relaxed">
                        Whether you&apos;re modernising a legacy system or
                        building from zero, we&apos;d love to learn more. We
                        usually reply within one business day.
                    </p>

                    <div className="mt-10 space-y-5">
                        <a
                            href="mailto:support@thooyra.in"
                            data-testid="contact-email"
                            className="flex items-center gap-4 p-4 rounded-xl border border-border hover:bg-secondary/60 transition-colors"
                        >
                            <Mail className="h-5 w-5" />
                            <div>
                                <div className="text-xs text-muted-foreground">
                                    Email
                                </div>
                                <div className="font-medium">
                                    support@thooyra.in
                                </div>
                            </div>
                        </a>
                        <a
                            href="https://wa.me/917975511832"
                            target="_blank"
                            rel="noreferrer"
                            data-testid="contact-whatsapp"
                            className="flex items-center gap-4 p-4 rounded-xl border border-border hover:bg-secondary/60 transition-colors"
                        >
                            <MessageCircle className="h-5 w-5" />
                            <div>
                                <div className="text-xs text-muted-foreground">
                                    WhatsApp
                                </div>
                                <div className="font-medium">
                                    Chat with our team
                                </div>
                            </div>
                        </a>
                        <a
                            href="https://calendly.com/"
                            target="_blank"
                            rel="noreferrer"
                            data-testid="contact-calendly"
                            className="flex items-center gap-4 p-4 rounded-xl border border-border hover:bg-secondary/60 transition-colors"
                        >
                            <Calendar className="h-5 w-5" />
                            <div>
                                <div className="text-xs text-muted-foreground">
                                    Schedule
                                </div>
                                <div className="font-medium">
                                    Book a 30-min intro call
                                </div>
                            </div>
                        </a>
                        <div className="flex items-center gap-4 p-4 rounded-xl border border-border">
                            <MapPin className="h-5 w-5" />
                            <div>
                                <div className="text-xs text-muted-foreground">
                                    Office
                                </div>
                                <div className="font-medium">
                                    Chennai, Tamil Nadu, India
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <motion.form
                    onSubmit={onSubmit}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    data-testid="contact-form"
                    className="lg:col-span-7 p-8 md:p-10 rounded-2xl border border-border bg-card"
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                            <label className="text-xs uppercase tracking-widest text-muted-foreground">
                                Name
                            </label>
                            <Input
                                data-testid="contact-input-name"
                                name="name"
                                value={form.name}
                                onChange={onChange}
                                placeholder="Your full name"
                                className="mt-2 h-12 bg-background"
                            />
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-widest text-muted-foreground">
                                Email
                            </label>
                            <Input
                                data-testid="contact-input-email"
                                name="email"
                                type="email"
                                value={form.email}
                                onChange={onChange}
                                placeholder="you@company.com"
                                className="mt-2 h-12 bg-background"
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label className="text-xs uppercase tracking-widest text-muted-foreground">
                                Company (optional)
                            </label>
                            <Input
                                data-testid="contact-input-company"
                                name="company"
                                value={form.company}
                                onChange={onChange}
                                placeholder="Your company"
                                className="mt-2 h-12 bg-background"
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label className="text-xs uppercase tracking-widest text-muted-foreground">
                                Project details
                            </label>
                            <Textarea
                                data-testid="contact-input-message"
                                name="message"
                                value={form.message}
                                onChange={onChange}
                                rows={6}
                                placeholder="Tell us a bit about what you're building or improving…"
                                className="mt-2 bg-background resize-none"
                            />
                        </div>
                    </div>
                    <div className="mt-8 flex items-center justify-between gap-4">
                        <p className="text-xs text-muted-foreground">
                            By submitting, you agree to be contacted by our
                            team about your enquiry.
                        </p>
                        <Button
                            data-testid="contact-submit"
                            type="submit"
                            disabled={submitting}
                            className="rounded-full h-12 px-6"
                        >
                            {submitting ? "Sending…" : "Send message"}
                            <Send className="ml-2 h-4 w-4" />
                        </Button>
                    </div>
                </motion.form>
            </div>
        </section>
    );
}
